import { timer } from 'rxjs';
import { map, takeWhile } from 'rxjs/operators';
import { LoadPolicyConstants } from '../constants/load-policy';
import { v4 as uuidv4 } from 'uuid';
import {
  CoverageTypeEnumSimple,
  CoverageTypeNames,
} from '../enums/coverage-type.enum';
import { environment } from '@env/environment';
import { Environment } from '../models/environment';

export class Utility {
  static isAbTest(testName: string) {
    return timer(0, 1000).pipe(
      takeWhile((seconds) => seconds < LoadPolicyConstants.AB_TEST_SECONDS),
      map(() => {
        const kameleoonVariation =
          sessionStorage.getItem('kameleoonVariation-' + testName) || '';
        return kameleoonVariation === testName;
      })
    );
  }

  static generateButtonIds() {
    setTimeout(() => {
      const buttons = document.querySelectorAll('main button');
      buttons.forEach((btn: HTMLButtonElement, key) => {
        const id =
          btn.innerText
            .trim()
            .replace(/\s+|[\r\n\t\f]+/g, '_')
            .replace(/\W+/g, '')
            .toLowerCase()
            .split('_')
            .slice(0, 6)
            .join('_') || `generated_id_${key}`;
        btn.id = btn.id || id;
      });
    });
  }

  static removeNullEntries(obj: any): any {
    Object.entries(obj).forEach(
      ([key, value]) =>
        (value && typeof value === 'object' && this.removeNullEntries(value)) ||
        ((value === null || value === '') && delete obj[key])
    );
    return obj;
  }

  static isEmptyObject(obj: any): boolean {
    return obj === null ||
      obj === undefined ||
      (Array.isArray(obj) && obj.length === 0)
      ? true
      : Object.entries(obj).length === 0 && obj.constructor === Object;
  }

  static getUniqueId(length = 9, isAllowSeparators = false): string {
    let uniqueId = uuidv4();
    if (!isAllowSeparators) {
      uniqueId = uniqueId.replace('-', '');
    }
    if (length) {
      uniqueId = uniqueId.substr(0, length);
    }
    return uniqueId;
  }

  static readonly getDriverXpertBrandName = (coverName: CoverageTypeNames) =>
    coverName === CoverageTypeNames.Drivexpert
      ? environment.brand_copy[coverName.toLowerCase()]
      : coverName;

  static appendEngineCapacityLabel(engineCapacity: string | number): string {
    const engineCapacityString = engineCapacity.toString() || '';
    return (
      engineCapacityString +
      (engineCapacityString.indexOf('.') !== -1 ||
      parseInt(engineCapacityString, 10) < 20
        ? 'L'
        : 'CC')
    );
  }

  static correctPostcodeFormat(value: string) {
    const postcodeFormatRegExp = /^(.*)(.{3})$/;
    const postcodeRegex = /\s+/g;

    if (!postcodeRegex.test(value)) {
      return value.replace(postcodeFormatRegExp, '$1 $2');
    }

    return value;
  }

  static isMta(): boolean {
    return window.location.pathname.includes('amend-policy');
  }

  static showGetAppCTA(brand: string): boolean {
    const isApp = localStorage.getItem('isApp');
    const showGetAppCTA = {
      churchill: true,
      directline: true,
      privilege: false,
      isApp: !isApp || isApp === 'false',
    };
    return showGetAppCTA[brand] && showGetAppCTA['isApp'];
  }

  static excludePromos<T>(premium, addProperties?: { [key: string]: T }) {
    if (!premium) return [];
    const offerings = premium?.lobData?.mOTLine_Ext?.offerings;
    const currentOffering = offerings.find((offering) => offering.coverages);
    const promos =
      currentOffering?.coverages?.vehicleCoverages[0]
        ?.automaticAppliedPromos_dlg;
    const filteredPromos = promos?.filter(
      (promo) => ['COMMSAC'].indexOf(promo?.code) === -1
    );
    if (addProperties) {
      return filteredPromos?.map((p) => {
        return {
          ...p,
          ...addProperties,
        };
      });
    }
    return filteredPromos ?? [];
  }

  static isPCW(state) {
    const offerings = state?.lobData?.mOTLine_Ext?.offerings || [];
    const offeringCode = offerings.filter(
      (o) => o?.periodPublicId_dlg === state?.selectedPeriodPublicId_dlg
    )[0]?.offeringCode_dlg;
    const channel = state?.baseData?.channel_dlg;
    return (
      [
        CoverageTypeEnumSimple.CompPcw,
        CoverageTypeEnumSimple.CompPlusPcw,
        CoverageTypeEnumSimple.EssentialsPcw,
      ].indexOf(offeringCode) > -1 || channel === 'pcw'
    );
  }

  static injectHeaderScripts(environment: Environment) {
    if (environment.tealiumSync) {
      Utility.headerScript({
        scriptContent: environment.tealiumSync,
        isAsync: false,
      });
    }
    if (environment.analyticsScript) {
      Utility.headerScript({ scriptContent: environment.analyticsScript });
    }
    if (environment.tealiumScript) {
      Utility.headerScript({
        scriptContent: `window.utag_cfg_ovrd = { noview : true };`,
        scriptType: 'inline',
      });
      Utility.headerScript({
        scriptContent: environment.tealiumScript,
        isAsync: false,
      });
    }
    if (environment.humanScript) {
      Utility.headerScript({ scriptContent: environment.humanScript });
    }
  }

  private static headerScript({
    scriptContent,
    scriptType = 'src',
    isAsync = true,
  }: {
    scriptContent: string;
    scriptType?: 'src' | 'inline';
    isAsync?: boolean;
  }) {
    const script = document.createElement('script');
    if (scriptType === 'src') {
      script.src = scriptContent;
      script.async = isAsync;
    } else {
      script.innerHTML = scriptContent;
    }
    document.head.appendChild(script);
  }
}
